import { useTranslation } from 'react-i18next'

import {
  CarouselProducts,
  EmptyState,
  PeriodDate,
  SelectStatus,
  SelectStatusOptionProps,
  SortBy,
  TableConfigurator,
} from '@/components'
import { DEFAULT_LIMIT_PAGE } from '@/constants'
import {
  formatCurrency,
  formatDate,
  formatLongDate,
  useParamsState,
} from '@/helpers'
import { ROUTES_ADMIN } from '@/router'
import {
  QUERIES_ADMIN,
  useSupplierList,
  useSupplierOrderEdit,
  useSupplierOrderList,
} from '@/services'
import {
  SaleOrderItemProps,
  SupplyOrderProps,
  SupplyOrderShipmentStatusEnum,
} from '@/types'
import {
  Button,
  Checkbox,
  CloseIcon,
  EditIcon,
  EyeIcon,
  FilterIcon,
  InputSearch,
  Pagination,
  Select,
  showToast,
  Tab,
  TableColumnsColumnProps,
  Tag,
  Text,
  Tooltip,
} from '@mercai/clever'
import { ReactNode, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  SupplierOrderDrawerFilter,
  SupplierOrderDrawerForm,
} from './components'

import {
  getBuyerName,
  getDestinationAddress,
  getSaleOrders,
} from './table-supply-order.constants'

import { TableSupplyOrderProps } from './table-supply-order.type'

import { useQueryClient } from '@tanstack/react-query'
import { addDays, format } from 'date-fns'
import styles from './table-supply-order.module.css'

export const TableSupplyOrder = ({
  filters = {},
  hiddenAppliedFilters = [],
  onSelectedSupplierOrdersChange,
  selectedSupplierOrders,
  withFilterByExpectedPaymentDate = false,
}: TableSupplyOrderProps) => {
  const { t: tEnums } = useTranslation('enums')
  const { t } = useTranslation('screenSupplierOrderManagementPage')

  const [filter, setFilter] = useParamsState({
    page: '1',
    limit: `${DEFAULT_LIMIT_PAGE}`,
    search: '',
    status: '',
    ordering: '-payments__expected_payment_date',
    hasTransaction: 'false',
    ...filters,
  })

  const { data: suppliersData } = useSupplierList({ params: { limit: 100 } })

  const supplierFiltered = useMemo(() => {
    return suppliersData?.data?.results?.find(
      (supplier) => supplier.email === filter?.supplier_Email,
    )
  }, [suppliersData, filter.supplier_Email])

  const { data, isLoading } = useSupplierOrderList({ ...filter }, true)

  const { mutateAsync: supplyOrderMutateAsync } = useSupplierOrderEdit()

  const [totalPages, setTotalPages] = useState(1)

  const [isOpenDrawerFilter, setIsOpenDrawerFilter] = useState(false)

  const [isOpenDrawerForm, setIsOpenDrawerForm] = useState(false)
  const [drawerFormUuid, setdrawerFormUuid] = useState('')

  const queryClient = useQueryClient()

  const changeShipmentStatus = async (
    uuid: string,
    shipmentStatus: SupplyOrderShipmentStatusEnum,
  ) => {
    const response = await supplyOrderMutateAsync({ uuid, shipmentStatus })
    if (!response?.success) {
      showToast({
        variant: 'error',
        title: response?.message,
      })
    }

    queryClient.invalidateQueries({
      queryKey: [QUERIES_ADMIN.SUPPLIER_ORDER.module],
    })
  }

  const handleOpenDrawerForm = (uuid: string) => {
    setdrawerFormUuid(uuid)
    setIsOpenDrawerForm(true)
  }

  const getContentTotalFiltered = () => {
    const activeFilters = Object.keys(filter)?.filter(
      (key) =>
        !!filter[key] &&
        !hiddenAppliedFilters.includes(key) &&
        key !== 'page' &&
        key !== 'limit' &&
        key !== 'ordering',
    ).length

    if (activeFilters > 0) {
      return (
        <Tag variant="primary" model="light" size="small">
          {activeFilters}
        </Tag>
      )
    }

    return ''
  }

  const handleFilter = (changedKey: string, newValue: string | number) => {
    if (changedKey === 'page') {
      setFilter({ ...filter, ...filters, [changedKey]: `${newValue}` })
    } else {
      setFilter({
        ...filter,
        ...filters,
        [changedKey]: `${newValue}`,
        page: '1',
      })
    }
  }

  const onHandleSortBy = (ordering: string) => {
    if (ordering === filter.ordering) {
      return handleFilter('ordering', '')
    }

    handleFilter('ordering', ordering)
  }

  const getFormattedFilter = (filterKey: string | number) => {
    if (hiddenAppliedFilters.includes(filterKey as string)) return null

    const filterString = filter?.[filterKey]

    if (filterKey === 'hasTransaction') {
      return `${t('tableSupplierOrderDrawerFilterHasTransaction')}: ${filterString === 'true' ? t('tableSupplierOrderDrawerFilterHasTransactionYes') : t('tableSupplierOrderDrawerFilterHasTransactionNo')}`
    }

    if (filterKey === 'supplier_Email') {
      return `${t('tableSupplierOrderDrawerFilterSupplier')}: ${supplierFiltered?.name || ''}`
    }

    if (filterKey === 'expectedPaymentDateBefore') {
      return `${t('tableSupplierOrderDrawerFilterCreatedAtBefore')} ${formatDate(filterString)}`
    }

    if (filterKey === 'expectedPaymentDateAfter') {
      return `${t('tableSupplierOrderDrawerFilterCreatedAtAfter')} ${formatDate(filterString)}`
    }

    if (filterKey === 'destinationAddress') {
      return `${t('tableSupplierOrderDrawerFilterDestinationAddress')}: ${filterString?.replace(/,/g, ', ')}`
    }

    if (filterKey === 'paymentPriority') {
      return `${t('tableSupplierOrderDrawerFilterPaymentPriority')}: ${tEnums(`supply_payment_priority_${filterString}`)}`
    }

    if (filterKey === 'shipmentStatus') {
      const result = filterString
        ?.split(',')
        .map((status) => tEnums(`supply_order_shipment_status_${status}`))
        .join(', ')

      return `${t('tableSupplierOrderDrawerFilterShipmentStatus')}: ${result}`
    }

    if (filterKey === 'status') {
      const result = filterString
        ?.split(',')
        .map((status) => tEnums(`supply_order_status_${status}`))
        .join(', ')

      return `${t('tableSupplierOrderDrawerFilterStatus')}: ${result}`
    }

    return filterString?.replace(/,/g, ', ')
  }

  const getContentFilter = () => {
    const filtersKeys = Object.keys(filter).filter(
      (item) =>
        !['limit', 'page', 'ordering'].includes(item) &&
        !hiddenAppliedFilters.includes(item) &&
        !!filter[item],
    )

    if (filtersKeys && filtersKeys.length === 0) {
      return null
    }

    return (
      <div className={styles['filtered-options']}>
        <Text>{t('filterApplied')}:</Text>

        {filtersKeys?.map((item) => (
          <Button
            onClick={() => handleFilter(item, '')}
            variant="light"
            size="small"
            key={item}
          >
            {getFormattedFilter(item)}

            <CloseIcon />
          </Button>
        ))}
      </div>
    )
  }

  const [selectedTab, setSelectedTab] = useState<string | undefined>(undefined)

  const onChangeSelectedTab = (tab: string) => {
    setSelectedTab(tab)

    if (['next14Days', 'next30Days', 'today', 'next7Days'].includes(tab)) {
      let selectedDate = new Date()
      const dateNow = new Date()

      let expectedPaymentDateAfter = ''
      let expectedPaymentDateBefore = ''

      const days: Record<string, number> = {
        today: 0,
        next7Days: 7,
        next30Days: 30,
        next14Days: 14,
      }

      selectedDate = addDays(dateNow, days[tab])

      selectedDate.setHours(23, 59, 59, 999)

      dateNow.setHours(0, 0, 0, 0)

      expectedPaymentDateAfter = format(dateNow, "yyyy-MM-dd'T'HH:mm:ss'Z'")

      expectedPaymentDateBefore = format(
        selectedDate,
        "yyyy-MM-dd'T'HH:mm:ss'Z'",
      )

      setFilter({
        ...filter,
        expectedPaymentDateBefore,
        expectedPaymentDateAfter,
      })
    } else if (tab === 'delayed') {
      const dateNow = new Date()
      dateNow.setHours(0, 0, 0, 0)

      const expectedPaymentDateBefore = format(
        dateNow,
        "yyyy-MM-dd'T'HH:mm:ss'Z'",
      )

      setFilter({
        ...filter,
        expectedPaymentDateBefore,
        expectedPaymentDateAfter: '',
      })
    } else {
      setFilter({
        ...filter,
        expectedPaymentDateBefore: '',
        expectedPaymentDateAfter: '',
      })
    }
  }

  const onChangePeriod = (startDate?: string, endDate?: string) => {
    setFilter({
      ...filter,
      expectedPaymentDateAfter: startDate || '',
      expectedPaymentDateBefore: endDate || '',
    })
  }

  const statuses: Record<string, ReactNode> = {
    pending: (
      <Tag variant="warning" model="light">
        {tEnums('supply_order_status_pending')}
      </Tag>
    ),
    completed: (
      <Tag variant="success" model="light">
        {tEnums('supply_order_status_completed')}
      </Tag>
    ),
    cancelled: (
      <Tag variant="danger" model="light">
        {tEnums('supply_order_status_cancelled')}
      </Tag>
    ),
  }

  const shipmentStatuses: SelectStatusOptionProps[] = [
    {
      label: tEnums('supply_order_shipment_status_in_collection'),
      value: 'in_collection',
      variant: 'primary',
    },
    {
      label: tEnums('supply_order_shipment_status_on_the_way'),
      value: 'on_the_way',
      variant: 'warning',
    },
    {
      label: tEnums('supply_order_shipment_status_not_started'),
      value: 'not_started',
      variant: 'danger',
    },
    {
      label: tEnums('supply_order_shipment_status_delivered'),
      value: 'delivered',
      variant: 'success',
    },
    {
      label: tEnums('supply_order_shipment_status_about_to_deliver'),
      value: 'about_to_deliver',
      variant: 'info',
    },
    {
      label: tEnums('supply_order_shipment_status_collection_tomorrow'),
      value: 'collection_tomorrow',
      variant: 'warning',
    },
    {
      label: tEnums('supply_order_shipment_status_crossing'),
      value: 'crossing',
      variant: 'basic',
    },
    {
      label: tEnums('supply_order_shipment_status_unloading'),
      value: 'unloading',
      variant: 'info',
    },
  ]

  const columns: TableColumnsColumnProps<SupplyOrderProps>[] = [
    {
      title: t('tableSupplierOrderColumnSupplier'),
      key: 'shipments.supplier',
      sortKey: 'supplier__name',
      render: ({ record }) => (
        <div className={styles['column-supplier']}>
          {onSelectedSupplierOrdersChange && (
            <Checkbox
              value={
                !!selectedSupplierOrders?.includes(record?.uuid || '') || false
              }
              onChange={() => onSelectedSupplierOrdersChange(record)}
            />
          )}
          <Tooltip text={record?.supplier?.name || '-'} orientation="top">
            <Link
              target="_blank"
              to={ROUTES_ADMIN.SUPPLY.SUPPLIERS.SUPPLIERS.SHOW.fullPath(
                record?.supplier?.uuid || '',
              )}
              className={styles.link}
            >
              {record?.supplier?.name || '-'}
            </Link>
          </Tooltip>
        </div>
      ),
    },
    {
      title: t('tableSupplierOrderColumnBuyerName'),
      key: 'companyName',
      width: '14rem',
      render: ({ record }) => (
        <div>
          <Tooltip
            text={getBuyerName(record?.items || [])}
            orientation="top"
            className={styles.tooltip}
          >
            <div className={styles['company-name']}>
              {getBuyerName(record?.items || [])}
            </div>
          </Tooltip>
        </div>
      ),
    },
    {
      title: 'SO`s',
      key: 'so',
      width: '14rem',
      render: ({ record }) => {
        const saleOrders =
          record?.saleOrders
            ?.map((saleOrder) => saleOrder?.reference)
            ?.join(', ') || '-'

        return (
          <div>
            <Tooltip
              text={saleOrders}
              orientation="top"
              className={styles.tooltip}
            >
              <div className={styles['company-name']}>{saleOrders}</div>
            </Tooltip>
          </div>
        )
      },
    },
    {
      title: t('tableSupplierOrderColumnSaleOrderPrice'),
      key: 'so-value',
      width: '12rem',
      render: ({ record }) => {
        const total = record?.saleOrders?.reduce((acc, saleOrder) => {
          return (
            acc +
            (record?.payments?.[0]?.currency === 'MXN'
              ? (+saleOrder?.totalOrderPrice || 0) *
                (+saleOrder?.payments?.[0]?.dollarExchangeRate || 1)
              : +saleOrder?.totalOrderPrice || 0)
          )
        }, 0)

        return (
          <div>
            {total
              ? formatCurrency(
                  `${total}`,
                  'es-MX',
                  record.payments?.[0]?.currency,
                )
              : '-'}
          </div>
        )
      },
    },
    {
      title: t('tableSupplierOrderColumnPrice'),
      key: 'value',
      width: '12rem',
      sortKey: 'payments__value',
      render: ({ record }) => {
        return (
          <div>
            {record?.total
              ? formatCurrency(
                  `${record.total}`,
                  'es-MX',
                  record.payments?.[0]?.currency,
                )
              : '-'}
          </div>
        )
      },
    },
    {
      title: t('tableSupplierOrderColumnReference'),
      key: 'reference',
      dataKey: 'reference',
      width: '9rem',
    },
    {
      title: t('tableSupplierOrderColumnDestinationAddress'),
      key: 'destinationAddress',
      width: '14rem',
      render: ({ record }) => {
        const destinationAddress = getDestinationAddress(record)
        return (
          <Tooltip
            text={destinationAddress}
            orientation="top"
            className={styles.tooltip}
          >
            <div className={styles['destination-address']}>
              {destinationAddress}
            </div>
          </Tooltip>
        )
      },
    },
    {
      title: t('tableSupplierOrderColumnExpectedPaid'),
      key: 'expectedPaymentDate',
      sortKey: 'payments__expected_payment_date',
      width: '12rem',
      render: ({ record }) =>
        record?.payments?.[0]?.expectedPaymentDate ? (
          formatLongDate(record?.payments?.[0]?.expectedPaymentDate)
        ) : (
          <Button
            variant="ghost"
            className={styles['button-expected-paid']}
            size="small"
            onClick={() => handleOpenDrawerForm(record?.uuid || '')}
          >
            {t('tableSupplierOrderColumnExpectedPaidButton')}
          </Button>
        ),
    },
    {
      title: t('tableSupplierOrderColumnPaymentPriority'),
      key: 'paymentPriority',
      sortKey: 'payments_priority_weight',
      width: '11rem',
      render: ({ record }) =>
        record?.payments?.[0]?.paymentPriority
          ? tEnums(
              `supply_payment_priority_${record?.payments?.[0]?.paymentPriority}`,
            )
          : '-',
    },
    {
      title: t('tableSupplierOrderColumnShipmentStatus'),
      key: 'shipmentStatus',
      width: '14.5rem',
      render: ({ record }) => (
        <SelectStatus
          size="medium"
          status={record?.shipmentStatus}
          options={shipmentStatuses}
          allowEmpty
          onChange={(status) =>
            changeShipmentStatus(
              record?.uuid || '',
              status as SupplyOrderShipmentStatusEnum,
            )
          }
        />
      ),
    },
    {
      title: t('tableSupplierOrderColumnStatus'),
      key: 'status',
      sortKey: 'status',
      width: '11rem',
      render: ({ record }) => statuses[record.status],
    },
    {
      title: t('tableSupplierOrderColumnPO'),
      key: 'po-reference',
      width: '9rem',
      render: ({ record }) => record?.supplyQuotation?.purchaseOrderCode || '-',
    },
  ]

  const actions: TableColumnsColumnProps<SupplyOrderProps> = {
    title: '',
    key: 'actions',
    width: '6.5rem',
    render: ({ record }) => (
      <div className={styles['column-actions']}>
        <Button
          variant="secondary"
          model="square"
          size="small"
          href={ROUTES_ADMIN.SUPPLY.ORDER_MANAGEMENT.PURCHASE_ORDER.UPDATE.fullPath(
            record?.uuid || '',
          )}
        >
          <EditIcon />
        </Button>
        <Button
          variant="primary"
          model="square"
          size="small"
          href={ROUTES_ADMIN.SUPPLY.ORDER_MANAGEMENT.PURCHASE_ORDER.SHOW.fullPath(
            record?.uuid || '',
          )}
        >
          <EyeIcon />
        </Button>
      </div>
    ),
  }

  const sorts = [
    {
      title: t('orderingCreatedAtButton'),
      sortBy: 'created_at',
    },
  ]

  const saleOrderStatuses: Record<string, ReactNode> = {
    created: (
      <Tag variant="warning" model="light" size="small">
        {tEnums('sale_order_status_created')}
      </Tag>
    ),
    processing: (
      <Tag variant="warning" model="light" size="small">
        {tEnums('sale_order_status_processing')}
      </Tag>
    ),
    completed: (
      <Tag variant="success" model="light" size="small">
        {tEnums('sale_order_status_completed')}
      </Tag>
    ),
    fraudulent: (
      <Tag variant="danger" model="light" size="small">
        {tEnums('sale_order_status_fraudulent')}
      </Tag>
    ),
    cancelled: (
      <Tag variant="danger" model="light" size="small">
        {tEnums('sale_order_status_cancelled')}
      </Tag>
    ),
  }

  const ExpandedComponent = ({ record }: { record: SupplyOrderProps }) => {
    return (
      <div className={styles['expanded-content']}>
        <div>
          <Text>
            <strong>
              {t('tableExpandedComponentSupplierOrderAssociatedOrders')}
            </strong>
          </Text>

          {getSaleOrders(record)?.map((item) => (
            <Text className={styles.order} key={item?.uuid}>
              <Tooltip text={item?.companyName || '-'}>
                <strong className={styles['tooltip-order']}>
                  {item?.companyName || '-'}
                </strong>
              </Tooltip>
              <Link
                to={`${ROUTES_ADMIN.ORDER_CENTER.ALL.fullPath()}?search=${item?.order}`}
                target="_blank"
                className={styles.link}
              >
                {item?.order || '-'}
              </Link>
              <Text variant="text-small">
                {item?.status ? saleOrderStatuses[item?.status] : '-'}
              </Text>
            </Text>
          ))}
        </div>
        <div>
          <Text>
            <strong>{t('tableExpandedComponentSupplierOrderDetails')}</strong>
          </Text>

          <Text className={styles.order}>
            <strong>
              {t('tableExpandedComponentSupplierOrderPaymentPriority')}
            </strong>
            {record?.payments?.[0]?.paymentPriority
              ? tEnums(
                  `supply_payment_priority_${record?.payments?.[0]?.paymentPriority}`,
                )
              : '-'}
          </Text>
          <Text className={styles.order}>
            <strong>
              {t('tableExpandedComponentSupplierOrderPOCreatedAt')}
            </strong>
            {record?.supplyQuotation?.createdAt
              ? formatDate(record?.supplyQuotation?.createdAt)
              : '-'}
          </Text>

          <Text className={styles.order}>
            <strong>
              {t('tableExpandedComponentSupplierOrderObservations')}
            </strong>
            {record?.observation || '-'}
          </Text>

          <Text className={styles.order}>
            <strong>
              {t('tableExpandedComponentSupplierOrderPaymentObservations')}
            </strong>
            {record?.payments?.[0]?.observation || '-'}
          </Text>
        </div>
        <div className={styles['expanded-content-products']}>
          <CarouselProducts
            items={(record?.items as unknown as SaleOrderItemProps[]) || []}
            dollarExchangeRate={
              +(record?.payments?.[0]?.dollarExchangeRate || 0)
            }
          />
        </div>
      </div>
    )
  }

  useEffect(() => {
    if (data?.data?.count) {
      setTotalPages(
        Math.ceil(data?.data?.count / +(filter?.limit || DEFAULT_LIMIT_PAGE)),
      )
    }
  }, [data])

  return (
    <div>
      {withFilterByExpectedPaymentDate && (
        <div className={styles.tabs}>
          <Tab
            value={selectedTab || 'all'}
            onChange={onChangeSelectedTab}
            items={[
              {
                label: t('tableSupplierOrderTabAll'),
                value: 'all',
              },
              {
                label: t('tableSupplierOrderTabDelayed'),
                value: 'delayed',
              },
              {
                label: t('tableSupplierOrderTabToday'),
                value: 'today',
              },
              {
                label: t('tableSupplierOrderTabNext7Days'),
                value: 'next7Days',
              },
              {
                label: t('tableSupplierOrderTabNext14Days'),
                value: 'next14Days',
              },
              {
                label: t('tableSupplierOrderTabPeriod'),
                value: 'period',
              },
            ]}
          />

          {selectedTab === 'period' && (
            <PeriodDate
              onPeriodApply={onChangePeriod}
              initialEndDate={filter?.expectedPaymentDateBefore}
              initialStartDate={filter?.expectedPaymentDateAfter}
            />
          )}
        </div>
      )}

      <TableConfigurator
        key={`@mercai/supply-order-center`}
        cookieKey={`@mercai/supply-order-center`}
        isLoading={!!isLoading}
        columns={columns}
        data={data?.data?.results || []}
        onSortBy={onHandleSortBy}
        sortBy={filter?.ordering}
        expandableComponent={({ record }) => (
          <ExpandedComponent record={record} />
        )}
        actionsColumn={actions}
        initialHidden={['destinationAddress']}
        header={
          <div>
            <div className={styles.filter}>
              <div className={styles['filter-group']}>
                <InputSearch
                  value={filter.search as string}
                  onChange={(value) => handleFilter('search', value)}
                  className={styles['search-input']}
                  translations={{
                    inputPlaceholder: t('tableSearchPlaceholder'),
                  }}
                />

                <Button
                  variant="secondary"
                  onClick={() => setIsOpenDrawerFilter(true)}
                >
                  <FilterIcon />
                  {t('tableFilterButton')}
                  {getContentTotalFiltered()}
                </Button>

                <SortBy
                  sortBy={filter?.ordering}
                  sorts={sorts}
                  onHandleSortBy={onHandleSortBy}
                />
              </div>
            </div>

            {getContentFilter()}
          </div>
        }
      />

      {!isLoading && (!data?.data || data?.data?.results?.length === 0) && (
        <EmptyState
          title={t('tableSupplierOrderEmptyTitle')}
          description={t('tableSupplierOrderEmptyDescription')}
        />
      )}

      {data?.data?.results && data.data.results.length >= 1 && (
        <div className={styles.pagination}>
          <div className={styles['pagination-show-registers']}>
            <Text>
              {t('tableShowRegisters', {
                showNumber:
                  (data?.data?.count || 0) < +filter.limit
                    ? data?.data?.count
                    : +filter.limit,
                totalNumber: data?.data?.count || 0,
              })}
            </Text>

            <Select
              value={filter.limit}
              onChange={(value) => handleFilter('limit', value)}
              options={[
                { label: '10', value: '10' },
                { label: '20', value: '20' },
                { label: '50', value: '50' },
                { label: '100', value: '100' },
              ]}
            />
          </div>

          <Pagination
            totalPages={totalPages}
            onChange={(page) => handleFilter('page', page)}
            currentPage={+filter.page as number}
          />
        </div>
      )}

      <SupplierOrderDrawerFilter
        filter={filter}
        onHandleFilter={setFilter}
        isOpen={isOpenDrawerFilter}
        onHandleOpen={() => setIsOpenDrawerFilter(false)}
        suppliersData={suppliersData?.data?.results || []}
      />

      <SupplierOrderDrawerForm
        isOpen={isOpenDrawerForm}
        onHandleOpen={() => setIsOpenDrawerForm(false)}
        uuid={drawerFormUuid}
      />
    </div>
  )
}
